import React, { useEffect, useState, useLayoutEffect, useContext} from "react";
import { useSelector, useDispatch } from "react-redux";
import LineGraph from "./component/LineGraph";
import Select from "components/select/select";
import { getMonthOptions, getDays } from "utils/constant";
import { urlSerializer } from "utils/utils";
import { SET_PROFILE_VIEWS } from "redux/modules/profile-view/types";
import moment from "moment";
import { gsap } from "gsap";
import SubscriptionViews from "./component/subscription-views";
import { SubscriptionContext } from "context/subscription";
import PopInfo from "components/info-popup/pop-info";
import MilestoneClicksGraph from "./component/MilestoneClicksGraph";
import { useTranslation } from "react-i18next";


export default function SettingsViews({ userData = null }) {
  const months = moment.months();
  const { http } = global.services;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isAllTime, setIsAllTime] = useState(false); // ← toggles All Time mode
  const [dateSelected, setDateSelected] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });
  const { isSubscribed, subscription } = useContext(SubscriptionContext);

  const {
    views = [],
    initialize: initializeProfileViews,
    refresh: refreshProfileViews,
  } = useSelector((state) => state.profileView);

  const getProfileViews = async () => {
    setLoading(true);
    try {
      // In All Time mode, send all_time=true and omit month/year so the
      // backend returns bucketed data instead of the per-day month results.
      const params = isAllTime
        ? { user_profile_id: userData.profile.id, all_time: true }
        : {
            user_profile_id: userData.profile.id,
            month: dateSelected.month,
            year: dateSelected.year,
          };

      const url = urlSerializer("public/profile-views", params);
      const { data } = await http.get(url);

      dispatch({
        type: SET_PROFILE_VIEWS,
        payload: {
          results: data.results,
          today: data.today,
          past30: data.past_30,
          total: data.total,
          // All Time extras — only present when all_time=true
          allTimeBuckets: data.all_time_buckets || null,
          first_view_at: data.first_view_at || null,
        },
      });
    } catch (err) {
      console.log("getProfileViews", err);
    } finally {
      setLoading(false);
    }
  };

  const onChangeDate = (e) => {
    const date = e.value.split(" ");
    const month = months.findIndex((value) => value === date[0]);
    setDateSelected({ year: date[1], month: month + 1 });
    setIsAllTime(false); // picking a month exits All Time mode
  };

  const onToggleAllTime = () => {
    setIsAllTime((prev) => !prev);
  };

  const dispatch = useDispatch();

  useEffect(() => {
    if (initializeProfileViews) getProfileViews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshProfileViews, initializeProfileViews]);

  // Re-fetch whenever the selected month/year changes OR All Time is toggled
  useEffect(() => {
    getProfileViews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateSelected, isAllTime]);

  useLayoutEffect(() => {
    gsap.fromTo(
      ".page-view-heading",
      { y: -20, opacity: 0 },
      { y: 0, opacity: 1 }
    );
    gsap.fromTo(
      ".page-view-graph",
      { scale: 0, opacity: 0 },
      { scale: 1, opacity: 1 }
    );
    gsap.fromTo(".page-view-item", { x: 50, opacity: 0 }, { x: 0, opacity: 1 });
  }, []);

  // In All Time mode the backend returns a pre-bucketed breakdown
  // (labels: ["Q1 2023", "Q2 2023", ...], data: [12, 45, ...]).
  // In normal mode we use the per-day results for the selected month.
  const chartLabels = isAllTime && views.allTimeBuckets
    ? views.allTimeBuckets.labels
    : getDays(dateSelected.year, dateSelected.month);

  const chartData = isAllTime && views.allTimeBuckets
    ? views.allTimeBuckets.data
    : views.results;

  return isSubscribed ? (
    <div>
      <div className="mt-6 w-full container-max-width bg-white rounded-md">
        {/* content */}
        <div className="w-full pt-12.5 pb-10 px-7.5 1md:px-24.5">
          <div className="w-full space-y-12.5 3md:flex 3md:items-center 3md:space-x-12.5 3md:space-y-0">
            <div className="w-full 3md:w-15/20">
              {/* headers */}
              <div className="page-view-heading w-full 2sm:flex 2sm:justify-between 2sm:items-center space">
                <div className="w-full flex flex-row gap-x-2 items-center">
                  <h1 className="text-lg leading-5.5 text-lightBlack font-semibold">
                    {t('pageView')}
                  </h1>
                  <PopInfo
                    containerClass="relative"
                    type="page_views"
                    responsive={{ left: "185px", right: "185px" }}
                  />
                </div>

                {/* Month dropdown + All Time button — always inline */}
                <div className="mt-4 flex flex-row flex-nowrap items-center gap-2 w-full 2sm:w-auto">
                  <div className="flex-shrink-0 mt-6" style={{ width: "180px" }}>
                    <Select
                      options={getMonthOptions(views.first_view_at)}
                      placeholder={t("selectDate")}
                      onChange={(e) => onChangeDate(e)}
                      height={40}
                      backgroundColor="#F2F2F2"
                      borderColor="#F2F2F2"
                      isDisabled={loading}
                    />
                  </div>
                  <button
                    onClick={onToggleAllTime}
                    disabled={loading}
                    className={`
                      whitespace-nowrap h-10 px-3.5 rounded-md text-sm font-semibold
                      flex-shrink-0 transition-colors duration-150
                      ${isAllTime
                        ? "bg-white text-black border border-gray-200 shadow-sm"
                        : "bg-thBg text-gray-100 hover:bg-white hover:text-black"
                      }
                    `}
                  >
                    {t("allTime")}
                  </button>
                </div>
              </div>

              {/* graph */}
              <div className="page-view-graph mt-7.5 w-full">
                <LineGraph
                  labels={chartLabels}
                  serverData={chartData}
                  year={dateSelected.year}
                  month={dateSelected.month}
                  isAllTime={isAllTime}
                />
              </div>
            </div>

            {/* right side (summary) — totals are always all-time figures so
                they don't change between normal and All Time mode */}
            <div className="w-full 3md:w-5/20">
              <div className="w-full gap-y-14 grid 1sm:grid-cols-3 1sm:gap-x-14 1sm:gap-y-0 3md:grid-cols-1 3md:gap-y-14 3md:gap-x-0">
                <div className="2sm:page-view-item w-full text-center 3md:text-left">
                  <p className="text-2xl leading-7.4 font-bold tracking-tighter text-lightBlack">
                    {views.today ?? 0}
                  </p>
                  <p className="text-base leading-6 text-darkerGray">
                    {t('pageViewsToday')}
                  </p>
                </div>

                <div className="page-view-item w-full text-center 3md:text-left">
                  <p className="text-2xl leading-7.4 font-bold tracking-tighter text-lightBlack">
                    {views.past30 ?? 0}
                  </p>
                  <p className="text-base leading-6 text-darkerGray">
                    {t('views30Days')}
                  </p>
                </div>

                <div className="page-view-item w-full text-center 3md:text-left">
                  <p className="text-2xl leading-7.4 font-bold tracking-tighter text-lightBlack">
                    {views.total ?? 0}
                  </p>
                  <p className="text-base leading-6 text-darkerGray">
                    {t('totalPageViews')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <MilestoneClicksGraph userData={userData} />
    </div>
  ) : (
    <SubscriptionViews
      year={dateSelected.year}
      month={dateSelected.month}
      subscription={subscription}
    />
  );
}
